import { useEffect, useContext, useRef } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, matchPath } from 'react-router-dom';

import { TickerContext } from 'components/TickerProvider';
import { diff } from 'lib/utils/date';
import { getCompany } from 'lib/selectors/getCompany';
import { getMachine } from 'lib/selectors/getMachine';
import { getPopup } from 'lib/selectors/getPopup';
import {
  actionClearDowntimeFlyup,
  actionClosePopup,
  actionTriggerDowntimeFlyup,
  actionSetPopupStack,
} from 'lib/actions';
import { getActiveUncategorizedInterval } from 'lib/selectors/getActiveUncategorizedInterval';
import { getJobStatus } from 'lib/selectors/getJobStatus';
import Routes from 'lib/utils/routes';
import { getUser } from 'lib/selectors/getUser';
import { getAreAnnotationsLoading } from 'lib/selectors/getAnnotations';
import { getDowntimeFlyup } from 'lib/selectors/getDowntimeFlyup';
import { getAreActivitiesEnabled } from 'lib/selectors/getAreActivitiesEnabled';
import { getIsLoadingJobRunASWrapper } from 'lib/selectors/getIsLoadingJobRunASWrapper';
import { getAnyOperatorFlowOpen } from 'lib/selectors/getAnyOperatorFlowOpen';
import { JOB_STATUSES } from 'lib/constants';

// The production-run overview overlay renders on top of the machine page while
// keeping it (and this watcher) mounted beneath, so the prompt/flyup must not
// fire there. It's the only route that keeps the watcher mounted; sign-in,
// select-job and the other routes swap the machine page out of the Switch, so
// the watcher is already unmounted on them and they need no pattern here.
// Operator-flow overlays that sit on top of the home route are gated separately
// via getAnyOperatorFlowOpen.
const SUPPRESSED_ROUTE_PATTERNS = [
  Routes.machineIdHomeTabCurrentOperationInfoPath.pattern,
];

const isSuppressedRoute = (pathname) => {
  return SUPPRESSED_ROUTE_PATTERNS.some((path) => {
    return Boolean(matchPath(pathname, { path, exact: false }));
  });
};

// When one operator flow closes and the next opens (e.g. submit-all row → labor
// ticket, clock-out modal → flow, sign-in → shift intro), React 16 doesn't
// batch the two state updates because the handoff happens after an await, in an
// embed message listener, or in a setTimeout. That leaves a render in between
// where every flag is false; without this grace the ticker-driven effect would
// fire the flyup in that gap. Keep suppressing for a moment after a flow closes
// so the next one can open.
const FLOW_CLOSE_GRACE_MS = 1000;

const useDowntimeWatcher = () => {
  const dispatch = useDispatch();
  const popup = useSelector(getPopup);
  const company = useSelector(getCompany);
  const history = useHistory();
  const nowMs = useContext(TickerContext);
  const activeUncategorizedInterval = useSelector(
    getActiveUncategorizedInterval
  );
  const machine = useSelector(getMachine);
  const jobStatus = useSelector(getJobStatus);
  const user = useSelector(getUser);
  const areAnnotationsLoading = useSelector(getAreAnnotationsLoading);
  const downtimeFlyup = useSelector(getDowntimeFlyup);
  const areActivitiesEnabled = useSelector(getAreActivitiesEnabled);
  const jobLoading = useSelector(getIsLoadingJobRunASWrapper);
  const operatorFlowOpen = useSelector(getAnyOperatorFlowOpen);

  // Record when an operator flow closes so the main effect can keep suppressing
  // through the unbatched handoff gap (see FLOW_CLOSE_GRACE_MS). Declared before
  // the main effect so the timestamp is updated before that effect reads it on
  // the same render.
  const flowClosedAtRef = useRef(0);
  const prevFlowOpenRef = useRef(operatorFlowOpen);
  useEffect(() => {
    if (prevFlowOpenRef.current && !operatorFlowOpen) {
      flowClosedAtRef.current = Date.now();
    }
    prevFlowOpenRef.current = operatorFlowOpen;
  }, [operatorFlowOpen]);

  useEffect(() => {
    (() => {
      if (!company || !machine || areAnnotationsLoading || jobLoading) return;

      if (
        !company.showDowntimeCategorizationFlyup &&
        !company.showInitialDowntimePrompt
      )
        return;

      if (!activeUncategorizedInterval && popup) {
        dispatch(actionClosePopup());
        dispatch(actionClearDowntimeFlyup());
        return;
      }

      if (jobStatus === JOB_STATUSES.SETUP) {
        if (popup) {
          dispatch(actionClosePopup());
          dispatch(actionClearDowntimeFlyup());
        }
        return;
      }

      // Don't interrupt an in-progress operator flow (labor ticket, sign-in,
      // clock-out, setup, shift handover/intro, or the APM job/work drawers), the
      // brief grace after one closes, or the production-run-overview route.
      // Guarding here — after the interval-ended / setup cleanup above but before
      // the flyup/popup triggers — means a stale popup still clears while
      // suppressed; we just never raise a new one during these actions.
      const inFlowCloseGrace =
        Date.now() - flowClosedAtRef.current < FLOW_CLOSE_GRACE_MS;
      if (
        operatorFlowOpen ||
        inFlowCloseGrace ||
        isSuppressedRoute(history.location.pathname)
      ) {
        return;
      }

      if (
        !activeUncategorizedInterval ||
        diff(new Date(), activeUncategorizedInterval.start, 'milliseconds') <
          company.downtimeUnderlayDelay
      )
        return;

      if (
        activeUncategorizedInterval &&
        company.showDowntimeCategorizationFlyup &&
        !downtimeFlyup.dismissed &&
        (!(popup && popup.dismissed) || company.mandatoryCategorization) &&
        diff(new Date(), activeUncategorizedInterval.start, 'milliseconds') >=
          company.downtimeFlyupDelay
      ) {
        dispatch(actionTriggerDowntimeFlyup());
        history.push({
          pathname: Routes.machineIdCategorizePath(machine.id),
          state: {
            preselected: activeUncategorizedInterval,
            returnPath: history.location.pathname,
          },
        });
      }

      if (popup && popup.dismissed) return;

      if (
        popup &&
        popup.machineId === machine.id &&
        popup.mandatory === company.mandatoryCategorization &&
        moment(popup.startAt).isSame(activeUncategorizedInterval.start)
      )
        return;

      if (company.showInitialDowntimePrompt) {
        dispatch(
          actionSetPopupStack({
            params: {
              machineId: machine.id,
              startAt: activeUncategorizedInterval.start,
              mandatory: company.mandatoryCategorization,
              popupType: 'downtime',
              order: 2,
            },
          })
        );
      }
    })();
  }, [
    company,
    machine,
    activeUncategorizedInterval,
    popup,
    dispatch,
    jobLoading,
    jobStatus,
    nowMs,
    history,
    user,
    areAnnotationsLoading,
    downtimeFlyup.dismissed,
    areActivitiesEnabled,
    operatorFlowOpen,
  ]);
};

export { useDowntimeWatcher };
